import { useEffect, useState } from 'react';
import { Button, Paper, Typography } from '@wbk/ui';
import { useRouter, useTranslation } from '@wbk/config';
import { CurrencyLabel } from '@/components/common/CurrencyLabel';

import type { ContentfulEvent } from '@wbk/contentful/api';

type Props = {
  event: ContentfulEvent;
};

const eventType = 'events';

const EventSidebarRS = ({ event }: Props) => {
  const { t } = useTranslation(['common', 'contentful_event', 'contentful_common']);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { locale } = useRouter();

  // Countdown timer logic
  useEffect(() => {
    if (
      !event?.schedule?.openDateTime ||
      new Date(event?.schedule?.openDateTime).getTime() - new Date().getTime() <= 0
    )
      return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const eventTime = new Date(event.schedule.openDateTime).getTime();
      const difference = eventTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [event?.schedule?.openDateTime]);

  const startingPrice = event?.startingPrice;
  //   const notifyMe = !!event.eventType?.includes('offline');

  const SecurePayment = () => {
    return (
      <div className='mx-auto flex w-fit items-center justify-center gap-1 rounded-lg bg-white/10 px-2 py-1'>
        <img src='/icons/SecureIcon.svg' alt='' className='h-5 w-5' />
        <Typography variant='body-XS'>{t('common:secure_checkout')}</Typography>
      </div>
    );
  };

  const CostSection = () => {
    return (
      <div className='flex flex-col items-end md:items-start'>
        {startingPrice > 0 && (
          <Typography variant='body-XXS' className='text-text-secondary'>
            {t('common:from')} <br />
          </Typography>
        )}
        <CurrencyLabel
          price={startingPrice}
          priceClassName='text-body-L font-bold'
          currencyClassName='text-body-L font-bold'
        />
        {startingPrice > 0 ? (
          <Typography variant='body-XXS' className='text-text-secondary'>
            {t('common:vat_included')}
          </Typography>
        ) : (
          ''
        )}
      </div>
    );
  };

  const CountdownSection = ({ label, value }: { label: string; value: string }) => {
    return (
      <div className='bg-body-level-1 flex h-[52px] w-[40px] flex-col items-center justify-center rounded px-1 py-2 text-center md:h-[79px] md:w-[72px] md:px-3'>
        <Typography variant='heading-M'>{value}</Typography>
        <Typography variant='body-XXS' className='text-text-secondary'>
          {label}
        </Typography>
      </div>
    );
  };

  const hasTimer =
    event?.schedule?.openDateTime &&
    new Date(event?.schedule?.openDateTime).getTime() - new Date().getTime() > 0;

  return (
    <div className='-mt-[15dvh] mb-20 space-y-6 lg:mb-0 lg:mt-0'>
      {/* Card 1: Book your spot */}
      {!hasTimer && (
        <Paper className='space-y-6 rounded-[14px] border-white/10 bg-black/5 !p-4 backdrop-blur-md md:!p-6'>
          <div className='flex items-start justify-between gap-2 md:block md:space-y-6'>
            <Typography as='h2' variant='heading-M'>
              {t('common:book_spot')}
            </Typography>

            <CostSection />
          </div>
          <Button
            className='w-full gap-2 py-4 lg:py-6 rtl:flex-row-reverse'
            phill
            as='a'
            href={
              process.env.VITE_PUBLIC_ENV !== 'production'
                ? `https://wbk-web-lap.webook.rocks/${locale}/${eventType}/${event.slug}/book`
                : `https://webook.com/${locale}/${eventType}/${event.slug}/book`
            }
          >
            <Typography strong className='md:text-body-L'>
              {event.buttonLabel}
            </Typography>
            <img src='/icons/TicketIcon.svg' alt='' className='size-4 lg:size-6 rtl:scale-x-[-1]' />
          </Button>

          <SecurePayment />
        </Paper>
      )}

      {/* if the event is not in the past, show the countdown timer} */}
      {hasTimer && (
        <>
          {/* Card 2: Until the event start */}
          <Paper className='space-y-6 rounded-[14px] border-white/10 bg-black/20 !p-4 backdrop-blur-md md:!p-6'>
            <Typography as='h2' variant='heading-M'>
              {t('common:until_event_start')}
            </Typography>

            <div className='flex items-start justify-between gap-2 md:block md:space-y-6'>
              <div className='flex items-center justify-center gap-2'>
                <div className='flex items-center justify-center gap-2'>
                  <CountdownSection
                    label={t('common:days')}
                    value={timeLeft.days.toString().padStart(2, '0')}
                  />
                  <Typography variant='heading-M'>:</Typography>
                  <CountdownSection
                    label={t('common:hours')}
                    value={timeLeft.hours.toString().padStart(2, '0')}
                  />
                  <Typography variant='heading-M'>:</Typography>
                  <CountdownSection
                    label={t('common:min')}
                    value={timeLeft.minutes.toString().padStart(2, '0')}
                  />
                  <Typography variant='heading-M'>:</Typography>
                  <CountdownSection
                    label={t('common:sec')}
                    value={timeLeft.seconds.toString().padStart(2, '0')}
                  />
                </div>
              </div>

              <Typography as='h2' variant='heading-M' className='my-2'>
                {startingPrice === 0 ? <>{t('common:free')}</> : <CostSection />}
              </Typography>
            </div>
            {typeof startingPrice === 'number' && startingPrice >= 0 && (
              <Button
                className='w-full gap-2 py-4 lg:py-6 rtl:flex-row-reverse'
                phill
                as='a'
                href={
                  process.env.VITE_PUBLIC_ENV !== 'production'
                    ? `https://wbk-web-lap.webook.rocks/${locale}/${eventType}/${event.slug}/book`
                    : `https://webook.com/${locale}/${eventType}/${event.slug}/book`
                }
              >
                <Typography strong className='md:text-body-L'>
                  {event.buttonLabel}
                </Typography>
                <img
                  src='/icons/TicketIcon.svg'
                  alt=''
                  className='size-4 lg:size-6 rtl:scale-x-[-1]'
                />
              </Button>
            )}

            <SecurePayment />
          </Paper>
        </>
      )}
    </div>
  );
};

export default EventSidebarRS;
